import type { ReactNode } from 'react';
import './Toast.css';

export interface ToastProps {
  /** The toast disappears by the caller clearing this — the prototype holds the
   * message in state and nulls it on a timer, and this component follows that
   * shape rather than owning its own timeout (a toast that dismisses itself
   * cannot be kept up by a caller who knows the message matters). */
  message: ReactNode | null;
  /** Optional trailing action — "View in history" in the prototype. */
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * The toast (design-system catalogue, Tier 2; census `toast`) — a dark
 * bottom-centred pill for the outcome of an action that has already happened
 * ("Sample rolled back — batch returned to review").
 *
 * `role="status"` / `aria-live="polite"`: an outcome announcement, not an alert —
 * it must not interrupt what a screen-reader user is in the middle of. The live
 * region is mounted permanently and the message swaps inside it, because a live
 * region that mounts *with* its first message is not reliably announced.
 */
export function Toast({ message, actionLabel, onAction }: ToastProps) {
  return (
    <div role="status" aria-live="polite">
      {message ? (
        <div className="dpr-toast">
          <span className="dpr-toast__text">{message}</span>
          {actionLabel && onAction ? (
            <button type="button" className="dpr-toast__action" onClick={onAction}>
              {actionLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
